'use client';

import type { AuthUser } from 'src/auth/context/auth-context-value';
import type { Theme, SxProps, Breakpoint } from '@mui/material/styles';

import { useRouter } from 'next/navigation';
import { useBoolean } from 'minimal-shared/hooks';

import Box from '@mui/material/Box';
import { Button, Skeleton } from '@mui/material';

import { paths } from 'src/routes/paths';

import { useHomeDemoExplore, HomeDemoExploreConfirmDialog } from 'src/hooks/use-home-demo-explore';

// import { usePathname } from 'src/routes/hooks';
import { Logo as LogoBase } from 'src/components/logo';

import { useAuthContext } from 'src/auth/hooks/use-auth-context';

import { NavMobile } from './nav/mobile';
import { NavDesktop } from './nav/desktop';
import { MenuButton } from '../components/menu-button';
import { HeaderSection } from '../core/header-section';
import { SignInButton } from '../components/sign-in-button';
// import { SettingsButton } from '../components/settings-button';
import { navData as mainNavData } from '../nav-config-main';
// import { SignOutButton } from '../components/sign-out-button';
// import { AccountDrawer } from '../components/account-drawer';
// import { AccountButton } from '../components/account-button';
import { AccountDrawer } from '../components/account-drawer';

import type { HeaderSectionProps } from '../core/header-section';

// ----------------------------------------------------------------------

// Logo 는 아직 JS 라 props 가 전부 필수로 추론된다 — 실제 계약(전부 선택)으로 좁혀 쓴다 (로직 무변)
const Logo = LogoBase as unknown as React.ComponentType<{
  sx?: SxProps<Theme>;
  href?: string;
  isSingle?: boolean;
  disabled?: boolean;
  className?: string;
}>;

type HeaderProps = HeaderSectionProps & {
  layoutQuery?: Breakpoint;
  slotProps?: { header?: HeaderSectionProps };
  user?: AuthUser | null;
  loading?: unknown;
};

export function Header({ sx, slotProps, layoutQuery = 'md', user, loading, ...other }: HeaderProps) {
  // const pathname = usePathname();
  const { value: open, onFalse: onClose, onTrue: onOpen } = useBoolean();

  const router = useRouter();
  // updateProfile 은 JS 시절부터 꺼내기만 하고 안 쓰던 값 — 삭제는 이 배치 계약 밖 (rename+타입만)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { authenticated, updateProfile } = useAuthContext();

  const {
    demoConfirmOpen,
    handleDemoExploreClick,
    handleCloseDemoConfirmDialog,
    handleConfirmDemoFromDialog,
  } = useHomeDemoExplore({
    sensorButtonLabel: '알아보기',
    onExecuteStart: onClose,
  });

  const headerSlots = {
    topArea: null, // Alert 제거
    leftArea: (
      <>
        {/** @slot Nav mobile */}
        <MenuButton
          onClick={onOpen}
          sx={(theme) => ({
            mr: 1,
            ml: 1,
            [theme.breakpoints.up(layoutQuery)]: { display: 'none' },
          })}
        />
        <NavMobile
          data={mainNavData}
          open={open}
          onClose={onClose}
          user={user}
          loading={loading}
          onDemoExploreClick={handleDemoExploreClick}
        />

        {/** @slot Logo - 데스크탑에서만 표시 */}
        <Logo
          isSingle={false}
          sx={(theme) => ({
            display: 'none',
            [theme.breakpoints.up(layoutQuery)]: {
              display: 'block',
              ml: '24px',
              flexShrink: 0,
              position: 'relative',
              zIndex: 2,
            },
          })}
        />
      </>
    ),
    centerArea: (
      <>
        {/** @slot Logo - 모바일에서만 표시 */}
        <Logo
          isSingle={false}
          sx={(theme) => ({
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            [theme.breakpoints.up(layoutQuery)]: {
              display: 'none',
            },
          })}
        />

        {/** @slot Nav desktop - 전체 너비 기준 가운데 정렬 */}
        <NavDesktop
          data={mainNavData}
          onDemoExploreClick={handleDemoExploreClick}
          sx={(theme) => ({
            display: 'none',
            [theme.breakpoints.up(layoutQuery)]: {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              position: 'absolute',
              left: 0,
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
            },
          })}
        />
      </>
    ),
    rightArea: (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1, md: 1.5 },
        mr: { xs: 2, md: 3 },
        flexShrink: 0,
        zIndex: 1, // 네비게이션 위에 표시되도록 z-index 추가
      }}>
        {/** @slot Settings button */}
        {/* <SettingsButton /> */}

        {/*
          🚨 **인증이 풀리기 전에는 둘 다 안 그린다.** `authenticated` 는 로딩 중 거짓이라
            그냥 갈라 쓰면 로그인한 사람이 잠깐 「50% 할인받기」(모르는 사람용 영업 CTA)를 본다.
            자리만 잡아 두면 값이 정해질 때 단추가 흔들림 없이 들어선다.
        */}
        {loading ? (
          <Skeleton
            variant="rounded"
            sx={{ width: { xs: 96, md: 100 }, height: HEADER_CTA_SX.height, borderRadius: 1 }}
          />
        ) : authenticated ? (
          // 들어가는 길이라 **채워서** 세운다 — 로그인한 사람에게 이 머리글의 주된 단추다
          <Button variant="contained"
            sx={{ display: { xs: 'none', md: 'block' } }}
            onClick={() => {
              router.push(paths.service.root);
            }}
          >
            워크스페이스
          </Button>
        ) : (
          // 모르는 사람에게 이 머리글의 주된 단추다 — **채워서** 세우고 로그인은 테두리로 한 단계 낮춘다 · 1+1 구독이라 「50% 할인」이다(요금 화면으로)
          // 폰에도 선다(광고 방문자 대부분이 폰) — 가운데 로고와 겹치지 않게 폰은 작은 단추
          <Button variant="contained"
            sx={HEADER_CTA_SX}
            onClick={() => {
              router.push(paths.subscription.plan);
            }}
          >
            50% 할인받기
          </Button>
        )}
        {/** @slot Sign in button */}
        {loading ? (
          <Skeleton variant="circular" width={40} height={40} />
        ) : !user ? (
          // 폰은 머리글에 「50% 할인받기」 하나만 — 로그인은 왼쪽 메뉴 안에 있다
          <SignInButton variant="outlined" color="inherit" sx={{ ...HEADER_CTA_SX, display: { xs: 'none', md: 'inline-flex' } }} />
        ) : (
          <AccountDrawer />
        )
        }
      </Box>
    ),
  };

  return (
    <>
      <HeaderSection
        layoutQuery={layoutQuery}
        {...slotProps?.header}
        slots={{ ...headerSlots, ...slotProps?.header?.slots }}
        slotProps={slotProps?.header?.slotProps}
        sx={sx}
        {...other}
      />
      <HomeDemoExploreConfirmDialog
        open={demoConfirmOpen}
        onClose={handleCloseDemoConfirmDialog}
        onConfirm={handleConfirmDemoFromDialog}
      />
    </>
  );
} 

/** 머리글 오른쪽 단추(50% 할인받기 · 로그인) — 폰은 가운데 로고와 겹치지 않게 낮고 좁게 */
const HEADER_CTA_SX = {
  whiteSpace: 'nowrap',
  height: { xs: 32, md: 36 },
  px: { xs: 1.25, md: 2 },
  fontSize: { xs: 13, md: 14 },
} as const;
